/**
 * ShareChatButton.jsx — one-tap "Share this chat" affordance for the chat
 * header. Calls POST /api/conversations/{id}/share (idempotent — returns the
 * same token every call), constructs a public /share/{token} URL, and
 * copies it to the clipboard.
 *
 * A tiny popover confirms the URL and offers Copy + Open + Revoke actions.
 */
import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import * as L from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

export default function ShareChatButton({ conversationId }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(null);

  const shareUrl = token ? `${window.location.origin}/share/${token}` : null;

  const ensureShare = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/conversations/${conversationId}/share`);
      setToken(data.token);
      return data.token;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create share link");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const onOpenChange = async (o) => {
    setOpen(o);
    if (o && !token) await ensureShare();
  };

  const copy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied");
    } catch (_) {
      // fallback: select-in-input; rare on modern browsers.
      toast.error("Couldn't copy — long-press the link to copy manually.");
    }
  };

  const openTab = () => {
    if (!shareUrl) return;
    window.open(shareUrl, "_blank", "noopener,noreferrer");
  };

  const revoke = async () => {
    setLoading(true);
    try {
      await api.delete(`/conversations/${conversationId}/share`);
      setToken(null);
      setOpen(false);
      toast.success("Share link revoked");
    } catch (_) {
      toast.error("Couldn't revoke");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-full border border-[var(--rammy-border)] hover:border-[var(--rammy-cyan)] hover:text-[var(--rammy-cyan-dark)] px-3 py-1.5 text-xs font-medium text-[var(--rammy-text-sec)] transition shrink-0"
          data-testid="chat-share"
          aria-label="Share this chat"
        >
          <L.Share2 size={13}/>
          <span className="hidden md:inline">Share</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 rounded-2xl border-[var(--rammy-border)] shadow-lg p-0 overflow-hidden" data-testid="chat-share-popover">
        <div className="p-4 space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-text-muted)]">Share this chat</div>
            <div className="text-xs text-[var(--rammy-text-sec)] mt-0.5 leading-relaxed">
              Anyone with the link can view — read-only. Revoke anytime.
            </div>
          </div>
          <div className="relative">
            <input
              readOnly
              value={loading ? "Creating link…" : (shareUrl || "")}
              onFocus={(e) => e.target.select()}
              className="w-full rounded-xl bg-[var(--rammy-surface-alt)] border border-[var(--rammy-border)] px-3 py-2 text-xs font-mono text-[var(--rammy-text)] focus:outline-none focus:border-[var(--rammy-cyan)]"
              data-testid="chat-share-url"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={copy}
              disabled={!shareUrl || loading}
              className="btn-pill btn-primary h-8 text-xs flex-1"
              data-testid="chat-share-copy"
            >
              <L.Copy size={12}/> Copy link
            </Button>
            <Button
              onClick={openTab}
              disabled={!shareUrl || loading}
              variant="outline"
              className="btn-pill h-8 text-xs"
              data-testid="chat-share-open"
            >
              <L.ExternalLink size={12}/>
            </Button>
          </div>
          {token && (
            <button
              onClick={revoke}
              disabled={loading}
              className="w-full text-[11px] text-[var(--rammy-text-muted)] hover:text-red-500 pt-1 transition"
              data-testid="chat-share-revoke"
            >
              Revoke this link
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
